/**
 * Les lectures de l'écran des journaux et des écritures, et elles seules.
 *
 * Aucun geste d'ici : ni validation, ni extourne, ni relance du journal des
 * ventes. Les vues sont en `security_invoker` : la RLS du noyau, réservée à
 * l'administration, s'applique à qui lit.
 *
 * ## La pagination n'est pas une commodité
 *
 * PostgREST plafonne une réponse à 1 000 lignes sans le dire (voir
 * `factures/gateway.supabase.ts`) : la liste est bornée par `range()`,
 * `count: 'exact'` rend le total réel, et le tri porte un départage stable
 * sur `id`. Mesuré en production le 27/09/2026, la vue calcule les 2 709
 * écritures du journal des ventes en une centaine de millisecondes, recherche
 * comprise : une pagination par décalage suffit à ce volume.
 */

import { db } from '../../payments/db';
import { enClair } from '../reprise/erreur';
import {
  COLONNES, lireRechercheEcritures, TRIS,
  type EcritureListee, type FiltresEcritures, type JournalDeLExercice, type LigneEcriture,
  type NatureEcriture, type StatutEcriture, type TypeDeJournal, type Ventilation,
} from './types';

const compta = () => db.schema('compta');

function echec(e: unknown, defaut: string): never {
  throw new Error(enClair(e, defaut));
}

/* PostgREST rend un `bigint` en nombre JSON ; les centimes d'Éclosion restent
   très en deçà de 2^53. */
const n = (v: unknown): number => (v === null || v === undefined ? 0 : Number(v));
const nn = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));
const s = (v: unknown): string | null => (v === null || v === undefined ? null : String(v));

/* Une liste de colonnes calculée (`COLONNES`) ne se type pas : le client rend
   alors un type d'erreur générique. Les formes sont déclarées à la main
   (`types.ts`) ; la conversion passe par `unknown`, en un seul endroit. */
const lignesBrutes = (data: unknown): Record<string, unknown>[] =>
  (Array.isArray(data) ? data : []) as Record<string, unknown>[];

/** 25 par page, comme l'écran des factures. */
export const TAILLE_DE_PAGE = 25;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ---------------------------------------------------------------------------
//  Exercices et journaux
// ---------------------------------------------------------------------------

export interface ExerciceTenu {
  annee: number;
  statut: string;
}

export async function lireLesExercices(): Promise<ExerciceTenu[]> {
  const { data, error } = await compta().from('exercices').select('annee, statut').order('annee', { ascending: false });
  if (error) echec(error, 'Exercices illisibles');
  return ((data ?? []) as Record<string, unknown>[]).map((e) => ({ annee: n(e.annee), statut: String(e.statut) }));
}

export async function lireLesJournaux(exercice: number): Promise<JournalDeLExercice[]> {
  const { data, error } = await compta()
    .from('v_journaux')
    .select(COLONNES.journaux.join(', '))
    .eq('exercice', exercice)
    .order('ordre', { ascending: true })
    .order('journal_code', { ascending: true });
  if (error) echec(error, 'Journaux illisibles');
  return lignesBrutes(data).map((j) => ({
    exercice: n(j.exercice),
    exerciceStatut: String(j.exercice_statut),
    code: String(j.journal_code),
    libelle: String(j.journal_libelle),
    type: j.journal_type as TypeDeJournal,
    compteTresorerie: s(j.compte_tresorerie_code),
    actif: Boolean(j.journal_actif),
    ordre: n(j.ordre),
    nbValidees: n(j.nb_validees),
    nbBrouillons: n(j.nb_brouillons),
    nbExtournes: n(j.nb_extournes),
    totalDebitCents: n(j.total_debit_cents),
    totalCreditCents: n(j.total_credit_cents),
    premierNumero: nn(j.premier_numero),
    dernierNumero: nn(j.dernier_numero),
    compteur: nn(j.compteur),
    numerotationContinue: Boolean(j.numerotation_continue),
    premiereDate: s(j.premiere_date),
    derniereDate: s(j.derniere_date),
    derniereValidation: s(j.derniere_validation),
  }));
}

/** Les journaux du plan, pour le filtre de la liste — actifs ou non. */
export async function lireLesCodesDeJournaux(): Promise<{ code: string; libelle: string }[]> {
  const { data, error } = await compta().from('journaux').select('code, libelle').order('ordre', { ascending: true });
  if (error) echec(error, 'Journaux illisibles');
  return ((data ?? []) as Record<string, unknown>[]).map((j) => ({ code: String(j.code), libelle: String(j.libelle) }));
}

// ---------------------------------------------------------------------------
//  La liste des écritures
// ---------------------------------------------------------------------------

const CHAMPS = COLONNES.liste.join(', ');

function versEcriture(e: Record<string, unknown>): EcritureListee {
  return {
    id: String(e.id),
    journal: String(e.journal_code),
    exercice: n(e.exercice),
    periode: n(e.periode),
    date: String(e.date_ecriture),
    numero: nn(e.numero),
    reference: s(e.reference),
    libelle: String(e.libelle),
    nature: e.nature as NatureEcriture,
    statut: e.statut as StatutEcriture,
    piece: s(e.piece_reference),
    sourceType: s(e.source_type),
    sourceId: s(e.source_id),
    sourceEvenement: s(e.source_evenement),
    sourceGeneration: n(e.source_generation),
    regleVersion: s(e.regle_version),
    processus: s(e.processus),
    dateOrigine: s(e.date_origine),
    exerciceOrigine: nn(e.exercice_origine),
    correctionAnterieure: Boolean(e.correction_anterieure),
    corrigeeId: s(e.ecriture_corrigee_id),
    extourneeParId: s(e.extournee_par_id),
    totalDebitCents: n(e.total_debit_cents),
    totalCreditCents: n(e.total_credit_cents),
    nbLignes: n(e.nb_lignes),
    comptes: Array.isArray(e.comptes) ? e.comptes.map(String) : [],
    tiersIds: Array.isArray(e.tiers_ids) ? e.tiers_ids.map(String) : [],
    tiersNoms: s(e.tiers_noms),
    factureNumero: s(e.facture_numero),
    creeLe: String(e.created_at),
    valideeLe: s(e.valide_le),
  };
}

export interface PageDEcritures {
  lignes: EcritureListee[];
  /** Le nombre réel d'écritures qui répondent aux filtres, pas celui affiché. */
  total: number | null;
}

export async function chargerEcritures(
  f: FiltresEcritures,
  depuis: number,
  taille = TAILLE_DE_PAGE,
): Promise<PageDEcritures> {
  let q = compta().from('v_liste_des_ecritures').select(CHAMPS, { count: 'exact' });
  if (f.exercice) q = q.eq('exercice', f.exercice);
  if (f.journal) q = q.eq('journal_code', f.journal);
  if (f.statut) q = q.eq('statut', f.statut);
  if (f.periode) q = q.eq('periode', f.periode);
  if (f.compte) q = q.contains('comptes', [f.compte]);
  const { motif } = lireRechercheEcritures(f.recherche);
  // Le joker de PostgREST est `*` ; le motif est nettoyé de sa syntaxe (`nettoyerPourIlike`).
  if (motif) q = q.ilike('recherche', `*${motif}*`);

  const ascendant = TRIS[f.tri].ascendant;
  const { data, error, count } = await q
    .order('date_ecriture', { ascending: ascendant })
    .order('journal_code', { ascending: true })
    .order('numero', { ascending: ascendant, nullsFirst: false })
    .order('id', { ascending: true })
    .range(depuis, depuis + taille - 1);
  if (error) echec(error, 'Écritures illisibles');
  return {
    lignes: lignesBrutes(data).map(versEcriture),
    total: count ?? null,
  };
}

// ---------------------------------------------------------------------------
//  Une écriture et ses lignes
// ---------------------------------------------------------------------------

function versVentilation(v: Record<string, unknown>): Ventilation {
  return {
    axeCode: String(v.axe_code),
    axeLibelle: String(v.axe_libelle ?? v.axe_code),
    valeurCode: String(v.valeur_code),
    valeurLibelle: String(v.valeur_libelle ?? v.valeur_code),
    montantCents: n(v.montant_cents),
  };
}

function versLigne(l: Record<string, unknown>): LigneEcriture {
  return {
    id: String(l.ligne_id),
    ecritureId: String(l.ecriture_id),
    position: n(l.position),
    compteCode: String(l.compte_code),
    compteLibelle: String(l.compte_libelle),
    classe: n(l.classe),
    tiersId: s(l.tiers_id),
    tiersNom: s(l.tiers_nom),
    tiersType: s(l.tiers_type),
    debitCents: n(l.debit_cents),
    creditCents: n(l.credit_cents),
    libelle: s(l.libelle),
    sourceType: s(l.source_type),
    sourceId: s(l.source_id),
    ligneCorrigeeId: s(l.ligne_corrigee_id),
    ventilations: Array.isArray(l.ventilations)
      ? (l.ventilations as Record<string, unknown>[]).map(versVentilation)
      : [],
  };
}

export interface EcritureDetaillee {
  ecriture: EcritureListee;
  lignes: LigneEcriture[];
  /** Les références de l'écriture extournée et de l'extourne, pour les nommer. */
  references: Record<string, string | null>;
}

/** `null` : aucune écriture ne porte cet identifiant (ou il n'en est pas un). */
export async function lireEcriture(id: string): Promise<EcritureDetaillee | null> {
  if (!UUID.test(id)) return null;
  const [entete, lignes] = await Promise.all([
    compta().from('v_liste_des_ecritures').select(CHAMPS).eq('id', id).maybeSingle(),
    compta().from('v_lignes_ecriture').select(COLONNES.lignes.join(', ')).eq('ecriture_id', id)
      .order('position', { ascending: true }).order('ligne_id', { ascending: true }),
  ]);
  if (entete.error) echec(entete.error, 'Écriture illisible');
  if (lignes.error) echec(lignes.error, 'Lignes illisibles');
  if (!entete.data) return null;

  const ecriture = versEcriture(entete.data as unknown as Record<string, unknown>);
  const liees = [ecriture.corrigeeId, ecriture.extourneeParId].filter((x): x is string => x !== null);
  const references: Record<string, string | null> = {};
  if (liees.length > 0) {
    const { data, error } = await compta().from('v_liste_des_ecritures').select('id, reference').in('id', liees);
    if (error) echec(error, 'Écritures liées illisibles');
    for (const r of (data ?? []) as Record<string, unknown>[]) references[String(r.id)] = s(r.reference);
  }
  return {
    ecriture,
    lignes: lignesBrutes(lignes.data).map(versLigne),
    references,
  };
}
